import { useState, useEffect, useRef, useCallback } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { QrCode, CheckCircle, Loader2, Clock, X, RefreshCw, Mail, User, Key, Send, Smartphone } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Unified Consent Collection Component
 * Supports both QR Code and OTP validation methods
 */
const ConsentCollectionModal = ({ 
  isOpen, 
  onClose, 
  onSuccess,
  titularData = null,
  showForm = true
}) => {
  const [step, setStep] = useState('form'); // 'form' | 'validation' | 'success'
  const [validationMethod, setValidationMethod] = useState('qr'); // 'qr' | 'otp'
  
  // QR State
  const [qrImageUrl, setQrImageUrl] = useState(null);
  const [qrToken, setQrToken] = useState(null);
  const [status, setStatus] = useState('idle'); // idle, generating, waiting, validated, expired, error
  const [timeRemaining, setTimeRemaining] = useState(900);
  
  // OTP State
  const [otpCode, setOtpCode] = useState('');
  const [otpSent, setOtpSent] = useState(false);
  const [otpLoading, setOtpLoading] = useState(false);
  const [otpValidated, setOtpValidated] = useState(false);
  const [otpToken, setOtpToken] = useState(null);
  
  const [loading, setLoading] = useState(false);
  const [consentPurposes, setConsentPurposes] = useState([]);
  
  // Multi-contact support
  const [selectedContacts, setSelectedContacts] = useState([]);
  const [availableContacts, setAvailableContacts] = useState([]);
  
  const [formData, setFormData] = useState({
    contacts: [],
    purposes: titularData?.purposes || [],
    validation_method: 'qr_code'
  });

  const pollingRef = useRef(null);
  const timerRef = useRef(null);

  // Build available contacts from titularData
  useEffect(() => {
    if (titularData) {
      const contacts = [];
      
      // Email
      if (titularData.email) {
        contacts.push({ type: 'email', label: 'Email', value: titularData.email, icon: 'mail' });
      }
      if (titularData.contact_value && titularData.contact_type === 'email' && titularData.contact_value !== titularData.email) {
        contacts.push({ type: 'email', label: 'Email', value: titularData.contact_value, icon: 'mail' });
      }
      
      // Phone
      if (titularData.phone) {
        contacts.push({ type: 'phone', label: 'Telefone', value: titularData.phone, icon: 'phone' });
      }
      
      // Mobile
      if (titularData.mobile) {
        contacts.push({ type: 'mobile', label: 'Telemóvel', value: titularData.mobile, icon: 'smartphone' });
      }
      
      // From contacts object (if present)
      if (titularData.contacts) {
        titularData.contacts.email?.forEach(e => {
          if (!contacts.find(c => c.value === e)) {
            contacts.push({ type: 'email', label: 'Email', value: e, icon: 'mail' });
          }
        });
        titularData.contacts.phone?.forEach(p => {
          if (!contacts.find(c => c.value === p)) {
            contacts.push({ type: 'phone', label: 'Telefone', value: p, icon: 'phone' });
          }
        });
        titularData.contacts.mobile?.forEach(m => {
          if (!contacts.find(c => c.value === m)) {
            contacts.push({ type: 'mobile', label: 'Telemóvel', value: m, icon: 'smartphone' });
          }
        });
      }
      
      setAvailableContacts(contacts);
      
      // Pre-select first contact
      if (contacts.length > 0) {
        setSelectedContacts([contacts[0]]);
        setFormData(prev => ({
          ...prev,
          contacts: [contacts[0]]
        }));
      }
    }
  }, [titularData]);

  // Fetch active consent purposes from API
  useEffect(() => {
    const fetchPurposes = async () => {
      try {
        const response = await axios.get(`${BACKEND_URL}/api/consent-purposes/active`, { withCredentials: true });
        setConsentPurposes(response.data || []);
      } catch (error) {
        console.error('Error fetching consent purposes:', error);
        setConsentPurposes([
          { purpose_id: 'newsletter', name: 'Newsletter', description: 'Receber newsletters periódicas' },
          { purpose_id: 'marketing', name: 'Marketing', description: 'Comunicações de marketing e promoções' },
          { purpose_id: 'profiling', name: 'Profiling', description: 'Análise de preferências para personalização' },
          { purpose_id: 'partners', name: 'Parceiros', description: 'Partilha de dados com parceiros autorizados' },
        ]);
      }
    };
    
    if (isOpen) {
      fetchPurposes();
    }
  }, [isOpen]);

  // Update form data when titularData changes
  useEffect(() => {
    if (titularData) {
      setFormData(prev => ({
        ...prev,
        purposes: titularData.purposes || prev.purposes
      }));
    }
  }, [titularData]);

  // Reset when modal opens
  useEffect(() => {
    if (isOpen) {
      setStep(showForm ? 'form' : 'validation');
      setStatus('idle');
      setQrImageUrl(null);
      setQrToken(null);
      setTimeRemaining(900);
      // Don't clear selectedContacts here - let titularData useEffect handle pre-selection
      // Only clear OTP state
      setOtpCode('');
      setOtpSent(false);
      setOtpValidated(false);
      setOtpToken(null);
      setValidationMethod('qr');
      
      // Re-initialize selected contacts from titularData when modal opens
      if (titularData) {
        const contacts = [];
        if (titularData.email) {
          contacts.push({ type: 'email', label: 'Email', value: titularData.email, icon: 'mail' });
        }
        if (titularData.phone) {
          contacts.push({ type: 'phone', label: 'Telefone', value: titularData.phone, icon: 'phone' });
        }
        if (titularData.mobile) {
          contacts.push({ type: 'mobile', label: 'Telemóvel', value: titularData.mobile, icon: 'smartphone' });
        }
        // Also check contacts object
        if (titularData.contacts) {
          titularData.contacts.email?.forEach(e => {
            if (!contacts.find(c => c.value === e)) {
              contacts.push({ type: 'email', label: 'Email', value: e, icon: 'mail' });
            }
          });
          titularData.contacts.phone?.forEach(p => {
            if (!contacts.find(c => c.value === p)) {
              contacts.push({ type: 'phone', label: 'Telefone', value: p, icon: 'phone' });
            }
          });
          titularData.contacts.mobile?.forEach(m => {
            if (!contacts.find(c => c.value === m)) {
              contacts.push({ type: 'mobile', label: 'Telemóvel', value: m, icon: 'smartphone' });
            }
          });
        }
        setAvailableContacts(contacts);
        // Pre-select first contact
        if (contacts.length > 0) {
          setSelectedContacts([contacts[0]]);
          setFormData(prev => ({
            ...prev,
            contacts: [contacts[0]]
          }));
        }
        
        if (!showForm) {
          generateQRCode();
        }
      }
    } else {
      cleanup();
      setSelectedContacts([]);
      setAvailableContacts([]);
    }
  }, [isOpen, titularData]);

  const cleanup = useCallback(() => {
    if (pollingRef.current) {
      clearInterval(pollingRef.current);
      pollingRef.current = null;
    }
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const handlePurposeChange = (purposeId, checked) => {
    setFormData(prev => ({
      ...prev,
      purposes: checked 
        ? [...prev.purposes, purposeId]
        : prev.purposes.filter(p => p !== purposeId)
    }));
  };

  const handleContactToggle = (contact) => {
    const isSelected = selectedContacts.some(c => c.value === contact.value);
    let newSelected;
    
    if (isSelected) {
      newSelected = selectedContacts.filter(c => c.value !== contact.value);
    } else {
      newSelected = [...selectedContacts, contact];
    }
    
    setSelectedContacts(newSelected);
    setFormData(prev => ({
      ...prev,
      contacts: newSelected
    }));
  };

  const validateForm = () => {
    if (selectedContacts.length === 0) {
      toast.error('Selecione pelo menos um tipo de contacto');
      return false;
    }
    if (formData.purposes.length === 0) {
      toast.error('Selecione pelo menos uma finalidade');
      return false;
    }
    return true;
  };

  const handleSubmitForm = () => {
    if (!validateForm()) return;
    setStep('validation');
    if (validationMethod === 'qr') {
      generateQRCode();
    }
  };

  // ====== QR CODE METHODS ======
  const generateQRCode = async () => {
    setLoading(true);
    setStatus('generating');
    setTimeRemaining(900);
    cleanup();

    try {
      const primaryContact = selectedContacts[0] || { type: 'email', value: titularData?.email || '' };
      
      const payload = {
        contact_type: primaryContact.type,
        contact_value: primaryContact.value,
        contacts: selectedContacts.map(c => ({ type: c.type, value: c.value })),
        purposes: formData.purposes.length > 0 ? formData.purposes : ['newsletter'],
        validation_method: 'qr_code',
        titular_id: titularData?.user_id || titularData?.titular_id || null
      };

      const response = await axios.post(
        `${BACKEND_URL}/api/qr/generate-for-consent`,
        payload,
        { withCredentials: true }
      );

      if (response.data.token && response.data.qr_image_url) {
        setQrToken(response.data.token);
        setQrImageUrl(response.data.qr_image_url);
        setStatus('waiting');
        startPolling(response.data.token);
        startTimer();
        toast.success('QR Code gerado! Aguardando validação pelo cliente...');
      } else {
        throw new Error('Invalid response');
      }
    } catch (error) {
      console.error('Error generating QR:', error);
      toast.error(error.response?.data?.detail || 'Erro ao gerar QR Code');
      setStatus('error');
    } finally {
      setLoading(false);
    }
  };

  const startPolling = (token) => {
    pollingRef.current = setInterval(async () => {
      try {
        const response = await axios.get(
          `${BACKEND_URL}/api/qr/check-validation/${token}`,
          { withCredentials: true }
        );

        if (response.data.validated) {
          cleanup();
          setStatus('validated');
          setStep('success');
          toast.success('Consentimento validado com sucesso!');
          
          setTimeout(() => {
            onSuccess?.(response.data.consent);
            onClose();
          }, 2500);
        } else if (response.data.expired) {
          cleanup();
          setStatus('expired');
        }
      } catch (error) {
        console.error('Polling error:', error);
      }
    }, 2000);
  };

  const startTimer = () => {
    timerRef.current = setInterval(() => {
      setTimeRemaining(prev => {
        if (prev <= 1) {
          cleanup();
          setStatus('expired');
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  // ====== OTP METHODS ======
  const handleSendOTP = async () => {
    const emailContact = selectedContacts.find(c => c.type === 'email') || availableContacts.find(c => c.type === 'email');
    
    if (!emailContact) {
      toast.error('É necessário um email para enviar o código OTP');
      return;
    }
    
    // Get purpose names for the email
    const purposeNames = formData.purposes.map(purposeId => {
      const purpose = consentPurposes.find(p => p.purpose_id === purposeId);
      return purpose?.name || purposeId;
    });
    
    // Get the primary contact value being consented
    const primaryContact = selectedContacts[0] || { type: 'email', value: emailContact.value };
    
    setOtpLoading(true);
    try {
      await axios.post(
        `${BACKEND_URL}/api/otp/request`,
        { 
          email: emailContact.value, 
          purpose: 'consent_collection',
          titular_id: titularData?.user_id || titularData?.titular_id,
          purposes: purposeNames,
          contact_type: primaryContact.type,
          contact_value: primaryContact.value
        },
        { withCredentials: true }
      );
      setOtpSent(true);
      toast.success('Código OTP enviado para ' + emailContact.value);
    } catch (error) {
      console.error('Error sending OTP:', error);
      toast.error(error.response?.data?.detail || 'Erro ao enviar OTP');
    } finally {
      setOtpLoading(false);
    }
  };

  const handleVerifyOTP = async () => {
    if (!otpCode || otpCode.length !== 6) {
      toast.error('Introduza o código OTP de 6 dígitos');
      return;
    }
    
    const emailContact = selectedContacts.find(c => c.type === 'email') || availableContacts.find(c => c.type === 'email');
    
    setOtpLoading(true);
    try {
      const response = await axios.post(
        `${BACKEND_URL}/api/otp/verify`,
        { 
          email: emailContact.value, 
          otp: otpCode 
        },
        { withCredentials: true }
      );
      
      setOtpToken(response.data.validation_token);
      setOtpValidated(true);
      toast.success('Código OTP validado com sucesso!');
    } catch (error) {
      console.error('Error verifying OTP:', error);
      toast.error(error.response?.data?.detail || 'Código OTP inválido');
    } finally {
      setOtpLoading(false);
    }
  };

  const handleSaveOTPConsent = async () => {
    if (!otpValidated || !otpToken) {
      toast.error('Valide o código OTP primeiro');
      return;
    }
    
    setLoading(true);
    try {
      const primaryContact = selectedContacts[0] || { type: 'email', value: titularData?.email || '' };
      
      await axios.post(
        `${BACKEND_URL}/api/titulares/${titularData?.user_id || titularData?.titular_id}/consents`,
        {
          contact_type: primaryContact.type,
          contact_value: primaryContact.value,
          contacts: selectedContacts.map(c => ({ type: c.type, value: c.value })),
          purposes: formData.purposes,
          validation_method: 'otp',
          validation_token: otpToken
        },
        { withCredentials: true }
      );
      
      setStep('success');
      toast.success('Consentimento registado com sucesso!');
      
      setTimeout(() => {
        onSuccess?.({ validation_method: 'otp' });
        onClose();
      }, 2500);
    } catch (error) {
      console.error('Error saving OTP consent:', error);
      toast.error(error.response?.data?.detail || 'Erro ao guardar consentimento');
    } finally {
      setLoading(false);
    }
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const handleRetry = () => {
    if (validationMethod === 'qr') {
      generateQRCode();
    } else {
      setOtpCode('');
      setOtpSent(false);
      setOtpValidated(false);
      setOtpToken(null);
    }
  };

  const handleCancel = () => {
    cleanup();
    onClose();
  };

  const handleBack = () => {
    cleanup();
    setStep('form');
    setStatus('idle');
    setOtpCode('');
    setOtpSent(false);
    setOtpValidated(false);
  };

  // Get email for OTP display
  const getOtpEmail = () => {
    const emailContact = selectedContacts.find(c => c.type === 'email') || availableContacts.find(c => c.type === 'email');
    return emailContact?.value || titularData?.email || '';
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && handleCancel()}>
      <DialogContent className="max-w-lg" data-testid="consent-collection-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {step === 'form' && <User className="w-5 h-5 text-[#3B82F6]" />}
            {step === 'validation' && (validationMethod === 'qr' ? <QrCode className="w-5 h-5 text-[#10B981]" /> : <Key className="w-5 h-5 text-[#3B82F6]" />)}
            {step === 'success' && <CheckCircle className="w-5 h-5 text-emerald-500" />}
            {step === 'form' ? 'Recolha de Consentimento' : 
             step === 'validation' ? (validationMethod === 'qr' ? 'Validação por QR Code' : 'Validação por OTP') : 
             'Consentimento Validado'}
          </DialogTitle>
          <DialogDescription>
            {step === 'form' && 'Preencha os dados e selecione as finalidades do consentimento'}
            {step === 'validation' && validationMethod === 'qr' && 'Peça ao cliente para ler o QR Code com o telemóvel'}
            {step === 'validation' && validationMethod === 'otp' && 'Introduza o código OTP enviado para o email do titular'}
            {step === 'success' && 'O consentimento foi registado com sucesso'}
          </DialogDescription>
        </DialogHeader>

        {/* STEP 1: Form */}
        {step === 'form' && (
          <div className="space-y-4 py-4">
            {/* Contact Selection - Multiple */}
            <div>
              <Label className="mb-2 block">Canais de Contacto *</Label>
              <div className="border rounded-lg p-3 bg-slate-50 space-y-2">
                {availableContacts.length > 0 ? (
                  availableContacts.map((contact, idx) => (
                    <div 
                      key={`${contact.type}-${idx}`} 
                      className={`flex items-center justify-between p-3 rounded-lg border cursor-pointer transition-colors ${
                        selectedContacts.some(c => c.value === contact.value)
                          ? 'bg-emerald-50 border-emerald-300'
                          : 'bg-white border-slate-200 hover:border-slate-300'
                      }`}
                      onClick={() => handleContactToggle(contact)}
                      data-testid={`contact-${contact.type}-${idx}`}
                    >
                      <div className="flex items-center gap-3">
                        <Checkbox
                          checked={selectedContacts.some(c => c.value === contact.value)}
                          onCheckedChange={() => handleContactToggle(contact)}
                        />
                        <div className="flex items-center gap-2">
                          {contact.type === 'email' && <Mail className="w-4 h-4 text-slate-500" />}
                          {contact.type === 'phone' && <User className="w-4 h-4 text-slate-500" />}
                          {contact.type === 'mobile' && <Smartphone className="w-4 h-4 text-slate-500" />}
                          <div>
                            <span className="text-sm font-medium">{contact.label}</span>
                            <span className="text-sm text-slate-600 ml-2">{contact.value}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="text-center py-4 text-slate-500">
                    <p className="text-sm">Nenhum contacto disponível para este titular.</p>
                    <p className="text-xs mt-1">Adicione contactos na ficha do titular.</p>
                  </div>
                )}
              </div>
              {selectedContacts.length > 0 && (
                <p className="text-xs text-emerald-600 mt-2">
                  {selectedContacts.length} canal(is) selecionado(s)
                </p>
              )}
            </div>

            {/* Purposes Selection */}
            <div>
              <Label className="mb-2 block">Finalidades do Consentimento *</Label>
              <div className="space-y-2 border rounded-lg p-3 bg-slate-50 max-h-48 overflow-y-auto">
                {consentPurposes.map(purpose => (
                  <div key={purpose.purpose_id} className="flex items-start space-x-3">
                    <Checkbox
                      id={`modal-${purpose.purpose_id}`}
                      checked={formData.purposes.includes(purpose.purpose_id)}
                      onCheckedChange={(checked) => handlePurposeChange(purpose.purpose_id, checked)}
                    />
                    <div className="grid gap-0.5 leading-none">
                      <label htmlFor={`modal-${purpose.purpose_id}`} className="text-sm font-medium cursor-pointer">
                        {purpose.name}
                      </label>
                      <p className="text-xs text-slate-500">{purpose.description}</p>
                    </div>
                  </div>
                ))}
                {consentPurposes.length === 0 && (
                  <p className="text-sm text-slate-500 text-center py-2">A carregar finalidades...</p>
                )}
              </div>
            </div>

            {/* Validation Method Selection */}
            <div>
              <Label className="mb-2 block">Método de Validação</Label>
              <RadioGroup value={validationMethod} onValueChange={setValidationMethod} className="flex gap-4">
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="qr" id="method-qr" />
                  <label htmlFor="method-qr" className="flex items-center gap-2 text-sm cursor-pointer">
                    <QrCode className="w-4 h-4 text-emerald-600" />
                    QR Code
                  </label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="otp" id="method-otp" />
                  <label htmlFor="method-otp" className="flex items-center gap-2 text-sm cursor-pointer">
                    <Key className="w-4 h-4 text-blue-600" />
                    Código OTP
                  </label>
                </div>
              </RadioGroup>
            </div>

            <div className="flex gap-3 pt-2">
              <Button variant="outline" className="flex-1" onClick={handleCancel}>
                Cancelar
              </Button>
              <Button 
                className={`flex-1 ${validationMethod === 'qr' ? 'bg-[#10B981] hover:bg-[#10B981]/90' : 'bg-[#3B82F6] hover:bg-[#3B82F6]/90'}`}
                onClick={handleSubmitForm}
                data-testid="submit-consent-form"
              >
                {validationMethod === 'qr' ? (
                  <>
                    <QrCode className="w-4 h-4 mr-2" />
                    Gerar QR Code
                  </>
                ) : (
                  <>
                    <Key className="w-4 h-4 mr-2" />
                    Validar com OTP
                  </>
                )}
              </Button>
            </div>
          </div>
        )}

        {/* STEP 2: Validation */}
        {step === 'validation' && (
          <Tabs value={validationMethod} onValueChange={setValidationMethod} className="py-4">
            <TabsList className="grid w-full grid-cols-2 mb-4">
              <TabsTrigger value="qr" className="flex items-center gap-2">
                <QrCode className="w-4 h-4" />
                QR Code
              </TabsTrigger>
              <TabsTrigger value="otp" className="flex items-center gap-2">
                <Key className="w-4 h-4" />
                Código OTP
              </TabsTrigger>
            </TabsList>

            {/* QR Code Tab */}
            <TabsContent value="qr" className="space-y-4">
              {/* Status Badge */}
              <div className="flex justify-center">
                {status === 'generating' && (
                  <Badge className="bg-blue-50 text-blue-700 px-4 py-2">
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    A gerar QR Code...
                  </Badge>
                )}
                {status === 'waiting' && (
                  <Badge className="bg-amber-50 text-amber-700 px-4 py-2">
                    <Clock className="w-4 h-4 mr-2" />
                    Aguardando validação - {formatTime(timeRemaining)}
                  </Badge>
                )}
                {status === 'expired' && (
                  <Badge className="bg-red-50 text-red-700 px-4 py-2">
                    <X className="w-4 h-4 mr-2" />
                    QR Code expirado
                  </Badge>
                )}
                {status === 'error' && (
                  <Badge className="bg-red-50 text-red-700 px-4 py-2">
                    <X className="w-4 h-4 mr-2" />
                    Erro ao gerar
                  </Badge>
                )}
                {status === 'idle' && !qrImageUrl && (
                  <Button onClick={generateQRCode} className="bg-[#10B981] hover:bg-[#10B981]/90">
                    <QrCode className="w-4 h-4 mr-2" />
                    Gerar QR Code
                  </Button>
                )}
              </div>

              {/* QR Code Display */}
              {(status !== 'idle' || qrImageUrl) && (
                <div className="flex justify-center">
                  <div className="border-4 border-slate-200 rounded-xl p-4 bg-white">
                    {(status === 'generating' || loading) ? (
                      <div className="w-48 h-48 flex items-center justify-center bg-slate-50 rounded">
                        <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
                      </div>
                    ) : status === 'expired' || status === 'error' ? (
                      <div className="w-48 h-48 flex items-center justify-center bg-red-50 rounded">
                        <X className="w-16 h-16 text-red-400" />
                      </div>
                    ) : qrImageUrl ? (
                      <img 
                        src={qrImageUrl} 
                        alt="QR Code para validação" 
                        className="w-48 h-48"
                        data-testid="qr-code-image"
                      />
                    ) : (
                      <div className="w-48 h-48 flex items-center justify-center bg-slate-50 rounded">
                        <QrCode className="w-16 h-16 text-slate-300" />
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Consent Summary - Selected Purposes */}
              <div className="bg-slate-50 rounded-lg p-3 space-y-2">
                <p className="text-sm text-slate-600">
                  <span className="font-medium">Contactos:</span> {selectedContacts.map(c => c.value).join(', ') || 'N/A'}
                </p>
                <div>
                  <p className="text-sm font-medium text-slate-700 mb-1">Finalidades do Consentimento:</p>
                  <div className="flex flex-wrap gap-1">
                    {formData.purposes.map(purposeId => {
                      const purpose = consentPurposes.find(p => p.purpose_id === purposeId);
                      return (
                        <Badge key={purposeId} variant="secondary" className="bg-emerald-100 text-emerald-700 text-xs">
                          {purpose?.name || purposeId}
                        </Badge>
                      );
                    })}
                    {formData.purposes.length === 0 && (
                      <span className="text-xs text-slate-500">Nenhuma finalidade selecionada</span>
                    )}
                  </div>
                </div>
              </div>

              {/* Instructions */}
              {status === 'waiting' && (
                <div className="text-center text-sm text-slate-500">
                  <p>O cliente deve apontar a câmara do telemóvel para o QR Code.</p>
                  <p className="mt-1 font-medium text-[#10B981]">A janela fechará automaticamente após validação.</p>
                </div>
              )}

              {/* Action Buttons */}
              <div className="flex gap-3">
                {status === 'expired' || status === 'error' ? (
                  <>
                    <Button variant="outline" className="flex-1" onClick={showForm ? handleBack : handleCancel}>
                      {showForm ? 'Voltar' : 'Cancelar'}
                    </Button>
                    <Button 
                      className="flex-1 bg-[#10B981] hover:bg-[#10B981]/90"
                      onClick={handleRetry}
                    >
                      <RefreshCw className="w-4 h-4 mr-2" />
                      Gerar Novo
                    </Button>
                  </>
                ) : (
                  <>
                    {showForm && (
                      <Button variant="outline" className="flex-1" onClick={handleBack} disabled={loading}>
                        Voltar
                      </Button>
                    )}
                    <Button 
                      variant="outline" 
                      className={showForm ? "flex-1" : "w-full"}
                      onClick={handleCancel}
                      disabled={loading}
                    >
                      Cancelar
                    </Button>
                  </>
                )}
              </div>
            </TabsContent>

            {/* OTP Tab */}
            <TabsContent value="otp" className="space-y-4">
              <div className="space-y-4">
                <p className="text-sm text-slate-600 text-center">
                  Envie um código OTP para o email do titular: <strong>{getOtpEmail()}</strong>
                </p>

                {!otpSent ? (
                  <Button 
                    onClick={handleSendOTP} 
                    className="w-full bg-[#3B82F6] hover:bg-[#3B82F6]/90"
                    disabled={otpLoading || !getOtpEmail()}
                    data-testid="send-otp-btn"
                  >
                    {otpLoading ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <Send className="w-4 h-4 mr-2" />
                    )}
                    Enviar Código OTP
                  </Button>
                ) : (
                  <div className="space-y-4">
                    <div className="p-3 bg-emerald-50 rounded-lg border border-emerald-200">
                      <p className="text-sm text-emerald-700 flex items-center gap-2">
                        <CheckCircle className="w-4 h-4" />
                        Código enviado para {getOtpEmail()}
                      </p>
                    </div>
                    
                    {!otpValidated ? (
                      <>
                        <div>
                          <Label>Código OTP</Label>
                          <Input
                            value={otpCode}
                            onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                            placeholder="000000"
                            className="text-center text-xl tracking-widest font-mono mt-1"
                            maxLength={6}
                            data-testid="otp-input"
                          />
                        </div>
                        <Button 
                          onClick={handleVerifyOTP} 
                          className="w-full bg-[#10B981] hover:bg-[#10B981]/90"
                          disabled={otpCode.length !== 6 || otpLoading}
                          data-testid="verify-otp-btn"
                        >
                          {otpLoading ? (
                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          ) : (
                            <CheckCircle className="w-4 h-4 mr-2" />
                          )}
                          Validar Código
                        </Button>
                        <Button 
                          variant="outline" 
                          onClick={handleSendOTP} 
                          className="w-full"
                          disabled={otpLoading}
                        >
                          <RefreshCw className="w-4 h-4 mr-2" />
                          Reenviar Código
                        </Button>
                      </>
                    ) : (
                      <div className="space-y-4">
                        <div className="text-center p-4 bg-emerald-50 rounded-lg">
                          <CheckCircle className="w-12 h-12 text-emerald-500 mx-auto mb-2" />
                          <p className="text-emerald-700 font-semibold">Identidade Verificada!</p>
                          <p className="text-sm text-slate-600 mt-1">Clique em confirmar para registar o consentimento.</p>
                        </div>
                        
                        <Button 
                          onClick={handleSaveOTPConsent}
                          className="w-full bg-[#10B981] hover:bg-[#10B981]/90"
                          disabled={loading}
                          data-testid="save-otp-consent-btn"
                        >
                          {loading ? (
                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          ) : (
                            <CheckCircle className="w-4 h-4 mr-2" />
                          )}
                          Confirmar Consentimento
                        </Button>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Consent Summary - Selected Purposes */}
              <div className="bg-slate-50 rounded-lg p-3 space-y-2">
                <p className="text-sm text-slate-600">
                  <span className="font-medium">Contactos:</span> {selectedContacts.map(c => c.value).join(', ') || 'N/A'}
                </p>
                <div>
                  <p className="text-sm font-medium text-slate-700 mb-1">Finalidades do Consentimento:</p>
                  <div className="flex flex-wrap gap-1">
                    {formData.purposes.map(purposeId => {
                      const purpose = consentPurposes.find(p => p.purpose_id === purposeId);
                      return (
                        <Badge key={purposeId} variant="secondary" className="bg-emerald-100 text-emerald-700 text-xs">
                          {purpose?.name || purposeId}
                        </Badge>
                      );
                    })}
                    {formData.purposes.length === 0 && (
                      <span className="text-xs text-slate-500">Nenhuma finalidade selecionada</span>
                    )}
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex gap-3">
                {showForm && (
                  <Button variant="outline" className="flex-1" onClick={handleBack}>
                    Voltar
                  </Button>
                )}
                <Button 
                  variant="outline" 
                  className={showForm ? "flex-1" : "w-full"}
                  onClick={handleCancel}
                >
                  Cancelar
                </Button>
              </div>
            </TabsContent>
          </Tabs>
        )}

        {/* STEP 3: Success */}
        {step === 'success' && (
          <div className="py-8 text-center">
            <div className="w-20 h-20 mx-auto mb-4 bg-emerald-100 rounded-full flex items-center justify-center">
              <CheckCircle className="w-10 h-10 text-emerald-500" />
            </div>
            <h3 className="text-xl font-semibold text-[#0F172A] mb-2">Consentimento Registado!</h3>
            <p className="text-slate-600">O consentimento foi validado e guardado com sucesso.</p>
            <p className="text-sm text-slate-500 mt-2">A fechar automaticamente...</p>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default ConsentCollectionModal;
